// modifySetOperator — VQE-Edit edit-action: switch the operator joining two
// branches of a flat UNION / UNION ALL / INTERSECT / EXCEPT (Oracle: MINUS)
// compound-SELECT chain via SR-Edit's `modify-set-operator` op. `branchIndex`
// is the SEPARATOR index (§2.3 S7 decision 2) — the operator BETWEEN
// `compoundBranches[branchIndex - 1]` (or the first branch, at index 0) and
// `compoundBranches[branchIndex]`, i.e. the one preceding
// `structure.compoundOperators[branchIndex]`. The UI's `separators[i]` and
// the resolver's `compoundOperators[i]` are 1:1 by construction (both scan
// depth-0 left-to-right over the same statement range) — see
// `setOperatorParity.test.ts`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — set-operator switch (§2.3 S7).

import type { QueryStructure, SetOperatorKind } from '../../../../sql-resolver/types';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type ModifySetOperatorOptions = {
  /** Optional structure used for separator-index bounds validation. */
  readonly structure?: QueryStructure;
};

export async function modifySetOperator(
  branchIndex: number,
  newOperator: SetOperatorKind,
  all: boolean,
  ctx: CommitEditCtx,
  options: ModifySetOperatorOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(branchIndex) || branchIndex < 0) {
    throw new Error(
      `modifySetOperator: branchIndex must be a non-negative integer (got ${branchIndex})`,
    );
  }
  if (options.structure) {
    const operators = options.structure.compoundOperators ?? [];
    if (operators.length === 0) {
      throw new Error('modifySetOperator: statement has no compound set-operator separators');
    }
    if (branchIndex >= operators.length) {
      throw new Error(
        `modifySetOperator: branchIndex ${branchIndex} out of range (chain has ${operators.length} separator(s))`,
      );
    }
    const current = operators[branchIndex];
    if (current?.kind === newOperator && current?.all === all) {
      return { status: 'applied' };
    }
  }
  return commitEdit({ op: 'modify-set-operator', branchIndex, newOperator, all }, ctx);
}
